import React, { useState, useEffect, useRef } from 'react';

interface SubtitleItem {
    startTime: number;
    endTime: number;
    text: string;
}

interface ActionDetails {
    [key: string]: string;
}

interface Action {
    name: string;
    details: ActionDetails;
}

interface SubtitlesProps {
    url: string;
    time: number;
    start?: boolean;
    callActions: (actions: Action[]) => void;
    setSubtitles?: (subtitles: SubtitleItem[]) => void;
    subtitles: SubtitleItem[];
}

const extractActions = (text: string): Action[] => {
    // Regex to match actions enclosed in square brackets
    const actionRegex = /\[(.*?)\]/g;
    let match;
    const actionsList: Action[] = [];

    // Extract all actions from the text
    while ((match = actionRegex.exec(text)) !== null) {
        const fullAction = match[1];

        // Find the action name (first word)
        const actionNameMatch = fullAction.match(/^(\S+)/);
        if (!actionNameMatch) continue;

        const actionName = actionNameMatch[1];

        // Extract parameters part (everything after the action name)
        const paramsText = fullAction.slice(actionName.length).trim();

        // Parse parameters
        const actionDetails: ActionDetails = {};

        // Custom parameter parsing that handles attributes with square brackets
        let currentPos = 0;
        let currentKey: string | null = null;
        let inQuotes = false;
        let currentValue = '';

        while (currentPos < paramsText.length) {
            const char = paramsText[currentPos];

            // Start of a new parameter
            if (currentKey === null && char !== ' ' && char !== '=') {
                currentKey = char;
                currentValue = '';
            }
            // Building the key name
            else if (currentKey !== null && char !== '=' && !inQuotes && char !== ' ') {
                currentKey += char;
            }
            // Found the equals sign
            else if (char === '=' && currentKey !== null) {
                // Next character will start the value
                currentPos++;

                // Check if the value starts with a quote
                if (currentPos < paramsText.length && paramsText[currentPos] === '"') {
                    inQuotes = true;
                    currentPos++; // Skip the opening quote
                }

                // Collect the value
                while (currentPos < paramsText.length) {
                    const valueChar = paramsText[currentPos];

                    // If we're in quotes, look for the closing quote
                    if (inQuotes) {
                        if (valueChar === '"' && paramsText[currentPos - 1] !== '\\') {
                            // End of quoted value
                            inQuotes = false;
                            break;
                        }
                        currentValue += valueChar;
                    }
                    // If not in quotes, look for space (end of unquoted value)
                    else if (valueChar === ' ') {
                        break;
                    }
                    else {
                        currentValue += valueChar;
                    }

                    currentPos++;
                }

                // Store the key-value pair
                if (currentKey) {
                    actionDetails[currentKey] = currentValue;
                }
                currentKey = null;
                currentValue = '';

                // Skip the closing quote if we were in quotes
                if (inQuotes) {
                    inQuotes = false;
                    currentPos++;
                }
            }

            currentPos++;
        }

        actionsList.push({ name: actionName, details: actionDetails });
    }

    return actionsList;
}

/**
 * Composant React qui lit un fichier SRT et affiche le sous-titre "le plus ancien"
 * correspondant au temps actuel.
 *
 * Props :
 * - url   : l'URL du fichier SRT à charger.
 * - start : booléen indiquant si le timer doit démarrer.
 */
const Subtitles: React.FC<SubtitlesProps> = ({ url, time, callActions, setSubtitles, subtitles }) => {
    const [lastActionTime, setLastActionTime] = useState<string | number>('');
    const executedActionsRef = useRef<Set<string>>(new Set());

    // Mise à jour du sous-titre courant en fonction du temps actuel
    useEffect(() => {
        // Récupérer tous les sous-titres dont l'intervalle inclut currentTime
        const matchingSubtitles = subtitles.find(
            s => time > s.startTime && time < s.endTime
        );

        // Sélectionner le sous-titre le plus ancien (celui avec le startTime le plus bas)
        if (matchingSubtitles) {
            // Execute and update actions
            const actions = extractActions(matchingSubtitles.text);
            if (actions && matchingSubtitles.startTime !== lastActionTime) {
                // Filter out actions that have already been executed
                const newActions = actions.filter(action => {
                    // Create a unique key for this action
                    const actionKey = `${matchingSubtitles.startTime}-${action.name}-${JSON.stringify(action.details)}`;
                    if (executedActionsRef.current.has(actionKey)) {
                        return false;
                    }
                    executedActionsRef.current.add(actionKey);
                    return true;
                });

                if (newActions.length > 0) {
                    callActions(newActions);
                }
                setLastActionTime(matchingSubtitles.startTime);
            }
        }
    }, [time, subtitles, callActions, lastActionTime]);

    return null;
};

export default Subtitles;
